import React, { useState, useRef } from "react";
import { PDFDocument } from "pdf-lib";
import { TrashIcon } from "../icons";
import S from "../styles";

export default function PDFCombinerPage() {
  const [files, setFiles] = useState([]); // { id, name, file, pageCount }
  const todayStr = () => { const d = new Date(); return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`; };
  const [exportName, setExportName] = useState(`combined_${todayStr()}`);
  const [selectedStore, setSelectedStore] = useState("");
  const [dragging, setDragging] = useState(false);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [combining, setCombining] = useState(false);
  const fileInputRef = useRef(null);

  const addFiles = async (newFiles) => {
    const entries = [];
    for (const file of newFiles) {
      if (file.type !== "application/pdf") continue;
      try {
        const bytes = await file.arrayBuffer();
        const pdf = await PDFDocument.load(bytes, { ignoreEncryption: true });
        entries.push({
          id: Date.now() + Math.random(),
          name: file.name,
          file,
          bytes,
          pageCount: pdf.getPageCount(),
        });
      } catch {
        entries.push({
          id: Date.now() + Math.random(),
          name: file.name,
          file,
          bytes: null,
          pageCount: 0,
          error: true,
        });
      }
    }
    setFiles((prev) => [...prev, ...entries]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const droppedFiles = [...e.dataTransfer.files];
    if (droppedFiles.length > 0) addFiles(droppedFiles);
  };

  const handleFileInput = (e) => {
    const selected = [...e.target.files];
    if (selected.length > 0) addFiles(selected);
    e.target.value = "";
  };

  const removeFile = (id) => setFiles(files.filter((f) => f.id !== id));
  const clearAll = () => setFiles([]);

  // Reorder via drag
  const handleRowDragStart = (idx) => setDragIdx(idx);
  const handleRowDragOver = (e, idx) => { e.preventDefault(); setDragOverIdx(idx); };
  const handleRowDragEnd = () => { setDragIdx(null); setDragOverIdx(null); };
  const handleRowDrop = (idx) => {
    if (dragIdx === null || dragIdx === idx) return;
    const updated = [...files];
    const [moved] = updated.splice(dragIdx, 1);
    updated.splice(idx, 0, moved);
    setFiles(updated);
    setDragIdx(null);
    setDragOverIdx(null);
  };

  // Move up/down
  const moveUp = (idx) => {
    if (idx === 0) return;
    const updated = [...files];
    [updated[idx - 1], updated[idx]] = [updated[idx], updated[idx - 1]];
    setFiles(updated);
  };
  const moveDown = (idx) => {
    if (idx >= files.length - 1) return;
    const updated = [...files];
    [updated[idx], updated[idx + 1]] = [updated[idx + 1], updated[idx]];
    setFiles(updated);
  };

  const combine = async () => {
    const validFiles = files.filter((f) => f.bytes && !f.error);
    if (validFiles.length === 0) return;
    setCombining(true);
    try {
      const merged = await PDFDocument.create();
      for (const entry of validFiles) {
        const donor = await PDFDocument.load(entry.bytes, { ignoreEncryption: true });
        const pages = await merged.copyPages(donor, donor.getPageIndices());
        pages.forEach((page) => merged.addPage(page));
      }
      const pdfBytes = await merged.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${exportName.trim() || "combined"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("PDF combine error:", err);
    }
    setCombining(false);
  };

  const totalPages = files.reduce((s, f) => s + (f.pageCount || 0), 0);

  return (
    <div>
      <div style={{ marginBottom: 4 }}>
        <h2 style={{ fontFamily: "'Archivo Black', 'Archivo', sans-serif", fontSize: 32, fontWeight: 900, color: "#0A0A0F", letterSpacing: "-0.02em", textTransform: "uppercase", lineHeight: 1, marginBottom: 4 }}>PDF Combiner</h2>
        <p style={{ fontSize: 14, color: "rgba(10,10,15,0.58)", marginBottom: 20 }}>Drag and drop PDFs to merge them into a single file</p>
      </div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 24 }}>
        <div style={S.statCard}><span style={S.statLabel}>Files</span><span style={S.statValue}>{files.length}</span></div>
        <div style={S.statCard}><span style={S.statLabel}>Total Pages</span><span style={S.statValue}>{totalPages}</span></div>
      </div>

      {/* Drop Zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => files.length === 0 && fileInputRef.current && fileInputRef.current.click()}
        style={{
          border: `2px dashed ${dragging ? "#0033FF" : "#0A0A0F"}`,
          borderRadius: 0,
          padding: files.length === 0 ? 60 : 20,
          textAlign: "center",
          background: dragging ? "rgba(200,149,108,0.06)" : "#FFFFFF",
          transition: "all .2s",
          cursor: files.length === 0 ? "pointer" : "default",
          marginBottom: 24,
        }}
      >
        {files.length === 0 ? (
          <div>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={dragging ? "#0033FF" : "rgba(10,10,15,0.58)"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 12 }}>
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="12" y1="18" x2="12" y2="12" />
              <line x1="9" y1="15" x2="12" y2="12" />
              <line x1="15" y1="15" x2="12" y2="12" />
            </svg>
            <div style={{ fontSize: 16, color: dragging ? "#0033FF" : "rgba(10,10,15,0.58)", fontWeight: 600, marginBottom: 6 }}>
              {dragging ? "Drop PDFs here" : "Drag & drop PDF files here"}
            </div>
            <div style={{ fontSize: 13, color: "rgba(10,10,15,0.58)" }}>or click to browse files</div>
          </div>
        ) : (
          <div>
            {/* File list */}
            <table style={{ ...S.table, minWidth: 0 }}>
              <thead>
                <tr>
                  <th style={{ ...S.th, width: "6%", textAlign: "center" }}>#</th>
                  <th style={{ ...S.th, width: "50%" }}>File Name</th>
                  <th style={{ ...S.th, width: "14%", textAlign: "center" }}>Pages</th>
                  <th style={{ ...S.th, width: "15%", textAlign: "center" }}>Reorder</th>
                  <th style={{ ...S.th, width: "10%", textAlign: "right" }}>Remove</th>
                </tr>
              </thead>
              <tbody>
                {files.map((f, idx) => (
                  <tr
                    key={f.id}
                    draggable
                    onDragStart={() => handleRowDragStart(idx)}
                    onDragOver={(e) => handleRowDragOver(e, idx)}
                    onDragEnd={handleRowDragEnd}
                    onDrop={() => handleRowDrop(idx)}
                    style={{
                      background: dragOverIdx === idx ? "rgba(200,149,108,0.1)" : f.error ? "rgba(239,68,68,0.08)" : "transparent",
                      cursor: "grab",
                      transition: "background .15s",
                    }}
                  >
                    <td style={{ ...S.td, textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: "rgba(10,10,15,0.58)" }}>{idx + 1}</td>
                    <td style={{ ...S.td, color: f.error ? "#ef4444" : "#0A0A0F", fontSize: 14 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={f.error ? "#ef4444" : "#0033FF"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                        </svg>
                        {f.name}
                        {f.error && <span style={{ fontSize: 11, color: "#ef4444" }}>(error reading)</span>}
                      </div>
                    </td>
                    <td style={{ ...S.td, textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 14, color: "#0A0A0F" }}>{f.pageCount || "—"}</td>
                    <td style={{ ...S.td, textAlign: "center" }}>
                      <button
                        onClick={() => moveUp(idx)}
                        disabled={idx === 0}
                        style={{ ...btnStyle, opacity: idx === 0 ? 0.3 : 1 }}
                        title="Move up"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="18 15 12 9 6 15" /></svg>
                      </button>
                      <button
                        onClick={() => moveDown(idx)}
                        disabled={idx >= files.length - 1}
                        style={{ ...btnStyle, opacity: idx >= files.length - 1 ? 0.3 : 1, marginLeft: 4 }}
                        title="Move down"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="6 9 12 15 18 9" /></svg>
                      </button>
                    </td>
                    <td style={{ ...S.td, textAlign: "right" }}>
                      <button onClick={() => removeFile(f.id)} style={S.iconBtn} title="Remove"><TrashIcon /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Add more button */}
            <div style={{ marginTop: 12, display: "flex", justifyContent: "center" }}>
              <button
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                style={{ ...S.addBtn, fontSize: 13 }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                Add More PDFs
              </button>
            </div>
          </div>
        )}
      </div>

      <input ref={fileInputRef} type="file" accept=".pdf" multiple onChange={handleFileInput} style={{ display: "none" }} />

      {/* Export controls */}
      {files.length > 0 && (
        <div style={{
          background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0,
          padding: 20, display: "flex", flexDirection: "column", gap: 16,
        }}>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 16, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 250 }}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2, color: "rgba(10,10,15,0.58)", marginBottom: 6 }}>
                Export File Name
              </label>
              <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
                <input
                  value={exportName}
                  onChange={(e) => { setExportName(e.target.value); setSelectedStore(""); }}
                  placeholder="combined"
                  style={{
                    ...S.input, marginBottom: 0, flex: 1, borderTopRightRadius: 0, borderBottomRightRadius: 0,
                    fontSize: 15, fontFamily: "'JetBrains Mono', monospace",
                  }}
                />
                <span style={{
                  padding: "10px 14px", background: "#F4F2EC", border: "2px solid #0A0A0F",
                  borderLeft: "none", borderRadius: 0, color: "rgba(10,10,15,0.58)",
                  fontSize: 15, fontFamily: "'JetBrains Mono', monospace",
                }}>
                  .pdf
                </span>
              </div>
            </div>
          </div>
          {/* Store name presets */}
          <div>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2, color: "rgba(10,10,15,0.58)", marginBottom: 8 }}>
              Store Preset
            </label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {[
                { key: "vv", label: "Vibrato Vault", prefix: "vv" },
                { key: "ds", label: "Design Shop", prefix: "ds" },
              ].map(({ key, label, prefix }) => (
                <button
                  key={key}
                  onClick={() => {
                    const date = todayStr();
                    setExportName(`${prefix}_${date}`);
                    setSelectedStore(key);
                  }}
                  style={{
                    padding: "8px 18px", borderRadius: 0, fontSize: 14, fontWeight: 600,
                    fontFamily: "'Archivo', sans-serif", cursor: "pointer", transition: "all .15s",
                    border: `1px solid ${selectedStore === key ? "#0033FF" : "#0A0A0F"}`,
                    background: selectedStore === key ? "rgba(200,149,108,0.12)" : "transparent",
                    color: selectedStore === key ? "#0033FF" : "rgba(10,10,15,0.58)",
                  }}
                >
                  {label}
                </button>
              ))}
              {selectedStore && (
                <button
                  onClick={() => { setSelectedStore(""); setExportName(`combined_${todayStr()}`); }}
                  style={{ ...S.clearBtn, padding: "8px 12px", fontSize: 13 }}
                >
                  Clear
                </button>
              )}
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button onClick={clearAll} style={{ ...S.clearBtn, padding: "10px 20px", fontSize: 14 }}>
              Clear All
            </button>
            <button
              onClick={combine}
              disabled={combining || files.filter((f) => !f.error).length === 0}
              style={{
                padding: "10px 24px", borderRadius: 0, border: "none", fontSize: 15, fontWeight: 600,
                fontFamily: "'Archivo', sans-serif", cursor: combining ? "wait" : "pointer",
                background: "#0033FF",
                color: "#FFFFFF", transition: "all .15s",
                opacity: combining ? 0.6 : 1,
              }}
            >
              {combining ? "Combining..." : `Combine & Download (${totalPages} pages)`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const btnStyle = {
  background: "none", border: "2px solid #0A0A0F", borderRadius: 0,
  padding: "4px 6px", cursor: "pointer", color: "rgba(10,10,15,0.58)",
  display: "inline-flex", alignItems: "center",
};
